import React, { useEffect, useState } from 'react';
import { useGate } from 'statsig-react';
import { proxy, useSnapshot } from 'valtio';
import type { Address } from 'viem';
import { useSwitchNetwork } from 'wagmi';
import { ERROR_TYPE, PILLARS, TRANSACTION_ERROR_ACTIONS } from '@soundxyz/utils/constants/logging';
import { AddressOrEnsInput } from '@components/AddressOrEnsInput';
import { EthInput } from '@components/EthInput';
import {
  BaseIndicator,
  EthereumIndicator,
  OptimismIndicator,
} from '@components/basics/ChainIndicators';
import { LoadingSpinner } from '@components/basics/LoadingSpinner';
import { Item, Select } from '@components/basics/forms/inputs/Select';
import { Button } from '@components/main-components/button/Button';
import { StandardModal } from '@components/main-components/modals/StandardModal';
import { useConnectedAuth } from '@context/auth/connected';
import { ModalContext } from '@context/modal';
import { useLogError } from '@hooks/useLogErrorNew';
import { useTransactionFlow } from '@hooks/useTransactionFlow';
import { AVAILABLE_CHAIN_IDS, FEATURE_GATES } from '@utils/constants';
import { humanReadableChainName } from '@utils/helpers';
import { useTransferFunds } from './useTransferFunds';

export const sendFundsStore = proxy<{ network: number; switchNetworkLoading: boolean }>({
  network: AVAILABLE_CHAIN_IDS.optimism,
  switchNetworkLoading: false,
});

export const SendFundsModal = () => {
  const { closeCancelModal } = ModalContext.useContainer();
  const logError = useLogError();
  const { activeChainId } = useConnectedAuth();

  const [amount, setAmount] = useState<string | null>('');
  const [recipientAddress, setRecipientAddress] = useState<Address | null>(null);
  const [desiredChainId, setDesiredChainId] = useState<number | null>(null);

  const {
    transferFunds,
    ethBalanceInEth,
    usdEthBalance,
    disabled,
    state,
    isLoading: isTransferLoading,
  } = useTransferFunds({
    amountInEth: amount ?? '0',
    recipientAddress,
    chainId: activeChainId.current || 1,
  });

  const getBalanceString = (balance?: string | null, usdBalance?: string) => {
    if (!balance) return undefined;
    return `Balance: ${balance} ETH ${!!usdBalance ? `($${usdBalance} USD)` : ''}`;
  };

  const { network: selectedNetwork } = useSnapshot(sendFundsStore);
  const { status, isLoading: isWagmiSwitchNetworkLoading } = useSwitchNetwork();
  const { isSwitchingNetwork } = useConnectedAuth();
  const { value: baseChain } = useGate(FEATURE_GATES.BASE_CHAIN);

  const switchNetworkLoading =
    isWagmiSwitchNetworkLoading || status === 'loading' || isSwitchingNetwork;

  const onNetworkSelect = useTransactionFlow({
    chainId: desiredChainId,
    onChainSwitchFail(error) {
      if (error) {
        logError({
          error: error,
          errorType: ERROR_TYPE.SWITCHING_CHAINS_ERROR,
          message: 'Error switching chains',
          toast: `Please switch network`,
          action: TRANSACTION_ERROR_ACTIONS.TRANSACTION_ERROR,
          level: 'info',
          isArtistOnlyPage: true,
          pillar: PILLARS.TRANSACTION,
        });
      }
      sendFundsStore.switchNetworkLoading = false;
      setDesiredChainId(null);
    },
    onAuth({ chainId }) {
      sendFundsStore.network = chainId;
    },
  });

  useEffect(() => {
    if (desiredChainId == null) return;

    onNetworkSelect();
  }, [desiredChainId, onNetworkSelect]);

  const body = (
    <div className="grid gap-6">
      <div className="grid gap-4">
        <div className="grid gap-1">
          <div className="text-base-l font-semibold text-black">Network</div>
          <Select
            disabled={switchNetworkLoading}
            value={selectedNetwork.toString()}
            onValueChange={value => {
              setDesiredChainId(Number(value));
            }}
          >
            <Item
              value={AVAILABLE_CHAIN_IDS.eth.toString()}
              className="mb-1 flex w-full cursor-pointer"
            >
              <div className="relative flex w-full items-center px-2">
                <div className="-ml-1.5 mr-2">
                  <EthereumIndicator />
                </div>
                <div className="text-left">
                  <p className="text-base-m font-semibold text-base800">
                    {humanReadableChainName(AVAILABLE_CHAIN_IDS.eth)}
                  </p>
                </div>
                {switchNetworkLoading && (
                  <div className="ml-1 flex">
                    <LoadingSpinner className="absolute left-1/2 top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 transform" />
                  </div>
                )}
              </div>
            </Item>
            {baseChain && (
              <Item
                value={AVAILABLE_CHAIN_IDS.base.toString()}
                className="mb-1 flex w-full cursor-pointer"
              >
                <div className="relative flex w-full items-center px-2">
                  <div className="-ml-1.5 mr-2 [&>svg]:h-[22px] [&>svg]:w-[22px]">
                    <BaseIndicator />
                  </div>
                  <div className="text-left">
                    <p className="text-base-m font-semibold text-base800">
                      {humanReadableChainName(AVAILABLE_CHAIN_IDS.base)}
                    </p>
                  </div>
                  {switchNetworkLoading && (
                    <div className="ml-1 flex">
                      <LoadingSpinner className="absolute left-1/2 top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 transform" />
                    </div>
                  )}
                </div>
              </Item>
            )}
            <Item
              value={AVAILABLE_CHAIN_IDS.optimism.toString()}
              className="flex w-full cursor-pointer"
            >
              <div className="relative flex w-full items-center px-2">
                <div className="-ml-1.5 mr-2">
                  <OptimismIndicator />
                </div>
                <div className="text-left">
                  <p className="text-base-m font-semibold text-base800">
                    {humanReadableChainName(AVAILABLE_CHAIN_IDS.optimism)}
                  </p>
                </div>
                {switchNetworkLoading && (
                  <div className="ml-1 flex">
                    <LoadingSpinner className="absolute left-1/2 top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 transform" />
                  </div>
                )}
              </div>
            </Item>
          </Select>
        </div>
        <div className="grid gap-1">
          <div className="text-base-l font-semibold text-black">Amount</div>
          <EthInput
            amount={amount}
            setAmount={setAmount}
            balance={getBalanceString(ethBalanceInEth, usdEthBalance)}
            disabled={switchNetworkLoading || isTransferLoading}
          />
        </div>
        <div className="grid gap-1">
          <div className="text-base-l font-semibold">Recipient</div>
          {!!ethBalanceInEth && (
            <AddressOrEnsInput inputDisabled={false} setAddress={setRecipientAddress} />
          )}
        </div>
      </div>
      <Button
        label={state}
        variant="primary"
        onClick={transferFunds}
        className="w-full flex-1"
        fullWidth
        disabled={disabled || switchNetworkLoading || !amount}
        loading={switchNetworkLoading}
      />
    </div>
  );

  return (
    <StandardModal
      title="Send ETH"
      closeModal={closeCancelModal}
      body={body}
      bodyClassName="pt-4 pb-1"
    />
  );
};
